import { olapicSSOClient } from 'olapic-sso-client';
/*
 * Allows the SSO server to interact with the SSO client helper service.
 *
 * @ngesdoc service
 * @ngname ssoClient
 * @ngmodule auth
 */
class SSOClient {
  /**
   * @param {ngLocation} $location To get the current URL and use it on the client configuration.
   * @param {Auth}       auth      To check if the user was authenticated with credentials.
   * @param {SSOState}   ssoState  To read the implementation parameters.
   */
  constructor($location, auth, ssoState) {
    'ngInject';

    /**
     * A local reference for the `auth` service.
     * @type {Auth}
     */
    this.auth = auth;
    /**
     * A local reference for the `ssoState` service.
     * @type {SSOState}
     */
    this.ssoState = ssoState;
    /**
     * This flag exists in order to prevent listeners for the "session created" event to trigger
     * redirections when the application just finished creating the cookie and it's being
     * redirected out or closed.
     *
     * @type {Boolean}
     */
    this.finished = false;
    /**
     * Configure the SSO client so it will use as API this same server (in case this is not the
     * one on production).
     */
    const host = $location.host();
    const apiURL = `https://${host}`;
    olapicSSOClient.setClientConfig({
      api: {
        url: apiURL,
      },
    });
  }
  /**
   * Asks the SSO client if there's a cookie and if it has clients in order to get the account for the client.
   * @return {?object}                    If there's information for the client on the cookie, this will
   *                                      be the options for the account selection.
   * @property {number}  accountId        The Photorank ID for the account selected on the client.
   * @property {boolean} canSwitchAccount Whether or not the user can switch the selected account.
   */
  getAccountForClient() {
    const clients = this._getHelper().getClients();
    return (clients && clients[this.ssoState.getState().client]) || null;
  }
  /**
   * Gets the SSO Client authentication reasons constant dictionary. This is used to generate
   * _"human readable messages"_ for the reasons and show them on the UI.
   * @return {Object}
   */
  getAuthenticationReasons() {
    return olapicSSOClient.AUTHENTICATION_REASONS;
  }
  /**
   * Asks the SSO client if there's a cookie and if it has a token in order to return it.
   * If the user was authenticated on an implementation that didn't require for him/her to select
   * an account, then the cookie exists, but there's no account information, and in order to avoid
   * having her/him repeat the whole process again, we just check if there's a token and send
   * him/her to the success screen right away, where he/she can select and account and go back
   * to the implementation.
   * @return {?string}
   */
  getToken() {
    return this._getHelper().getToken();
  }
  /**
   * Validates if a given callback URL can implement SSO, as it needs to match with the domain
   * that will be used for the cookie.
   * @param {string} url  The URL to validate
   * @return {Boolean}
   */
  isCallbackURLValid(url) {
    return this._getHelper().isCallbackURLValid(url);
  }
  /**
   * Adds a listener function for the event that gets triggered when the cookie is created. This
   * is used in case the user is on the process of being authenticated and the cookie was created
   * by another implementation, so the app can redirect the user directly to the success screen.
   * @param  {function} fn The function to execute when the event is triggered.
   * @return {function} A function to remove the listener.
   */
  onSessionCreated(fn) {
    const helper = this._getHelper();
    helper.startWatchingTheCookie();
    return olapicSSOClient.once(olapicSSOClient.EVENTS.session.created, () => {
      helper.stopWatchingTheCookie();
      fn();
    });
  }
  /**
   * Adds a listener function for the event that gets triggered when the cookie is removed.
   * @param  {function} fn The function to execute when the event is triggered.
   * @return {function} A function to remove the listener.
   */
  onSessionInterrupted(fn) {
    this._getHelper().startWatchingTheCookie();
    return olapicSSOClient.once(olapicSSOClient.EVENTS.session.interrupted, fn);
  }
  /**
   * This is called when one of the requests on the authentication process returns an unauthorized
   * response. It deletes the cookie without revoking the token, since it's already invalid.
   */
  signout() {
    return this._getHelper().signout();
  }
  /**
   * This is called when the user wants to stop the authentication process and signout instead.
   * The method will first check if there's a cookie and token (with `getToken`) and if that's the
   * case, it will use the SSO Client `signout` method to revoke the token, otherwise, it will just
   * return an already resolved promise since there's no token nor cookie saved to delete/revoke.
   * @return {Promise<undefined,Error>}
   */
  signoutAndRevoke() {
    return this.getToken() ?
      olapicSSOClient.signout() :
      Promise.resolve();
  }
  /**
   * Informs the implementation that the authentication was a success.
   * @param {string}  token                    The authentication token obtained after the process.
   * @param {string}  sessionId                A unique ID for the user session. This will
   *                                           eventually be used to keep track if the user
   *                                           information changed in order to clear the client
   *                                           cache.
   * @param {string}  userId                   The ID of the authenticated user.
   * @param {number}  [accountId=0]            A Photorank's account ID, in case the implementation
   *                                           needed one.
   * @param {boolean} [canSwitchAccount=false] Whether or not the user can switch accounts on the
   *                                           implementation.
   */
  success(token, sessionId, userId, accountId = 0, canSwitchAccount = false) {
    this.finished = true;
    const helper = this._getHelper();
    const { callback, client } = this.ssoState.getState();
    this.ssoState.destroyState();
    const data = {
      token,
      client,
      sessionId,
      userId,
      accountId,
      canSwitchAccount,
      withCredentials: this.auth.isTheUserAuthenticatedWithCredentials(),
    };

    if (callback) {
      helper.redirect(callback, data);
    } else {
      helper.message(data);
    }
  }
  /**
   * Gets access to the SSO Client helper service.
   * @return {SSOServerHelper}
   * @access protected
   * @ignore
   */
  _getHelper() {
    return olapicSSOClient.get('ssoServerHelper');
  }
}

export default SSOClient;
